<script lang="ts">
  // Maßnahmen-Popover: Trigger + Liste anwendbarer Maßnahmen (aus der
  // remediations-Registry), jede mit Klartext-Konsequenz und entweder einer
  // direkten Aktion oder Dauer-Presets. Ein Bedienelement für drei Orte --
  // gesteuert über triggerVariant:
  //   - "pill":     Text-Button neben Geschwister-Buttons (Host-Einstellungen)
  //   - "inline":   dezenter Text-Trigger in einer Kopfzeile (Cluster-Seite)
  //   - "critical": Affordance an einem kritischen Event (Dashboard-Lane)
  //   - "symbol":   nur ein Symbol in der Titelzeile einer Tabelle, Name im
  //                 Tooltip (docs/gestaltung.md, Regel 5; Cluster-Seite seit
  //                 29.09.2026 -- vorher "inline" mit Text)
  // Die eigentlichen Folgen kennt die Maßnahme selbst (consequence), der Wirk-
  // pfad liegt im Backend (entity_disables-Gates) -- hier nur der kurze Weg.
  //
  // Das Panel haengt per anchoredPopover am <body> (gleiche Begruendung
  // wie in DisableMenu: clippende Karten-/Tabellenvorfahren, in WebKit
  // auch senkrecht) und klappt nach oben, wenn unten kein Platz ist.

  import { anchoredPopover } from "./anchored-popover";
  import { t } from "./i18n.svelte";
  import Icon, { type IconName } from "./Icon.svelte";

  import { portal } from "./portal";
  import type { Remediation, RemediationVariant } from "./remediations.svelte";
  import { toast } from "./toast.svelte";

  interface Props {
    remediations: Remediation[];
    label: string;
    triggerVariant?: "pill" | "inline" | "critical" | "symbol";
    align?: "left" | "right";
    /** nur bei triggerVariant "symbol" */
    icon?: IconName;
  }

  let {
    remediations,
    label,
    triggerVariant = "inline",
    align = "right",
    icon = "bell-off",
  }: Props = $props();

  let open = $state(false);
  let busy = $state(false);
  let trigger = $state<HTMLButtonElement | null>(null);

  const triggerClass = $derived(
    triggerVariant === "pill"
      ? "text-fg-muted hover:bg-bg-muted/60 hover:text-fg border border-border rounded-md px-3 py-1 text-xs transition"
      : triggerVariant === "critical"
        ? "text-danger border-danger/40 hover:bg-danger/10 inline-flex items-center gap-1 rounded border px-2 py-1 t-meta font-medium transition"
        : triggerVariant === "symbol"
          ? "text-fg-subtle hover:bg-fg/8 hover:text-fg inline-grid size-9 shrink-0 place-items-center rounded-lg transition"
          : "text-fg-subtle hover:text-fg inline-flex items-center gap-1 t-meta transition",
  );

  function toggle(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    open = !open;
  }

  function close() {
    open = false;
  }

  async function exec(r: Remediation, variant?: RemediationVariant) {
    busy = true;
    try {
      await r.run(variant);
      open = false;
    } catch (err) {
      toast.error(
        t("remediation.failed", "Maßnahme fehlgeschlagen: {err}").replace(
          "{err}",
          err instanceof Error ? err.message : String(err),
        ),
      );
    } finally {
      busy = false;
    }
  }
</script>

<div class="relative inline-flex">
  <button bind:this={trigger} type="button" onclick={toggle} class={triggerClass}
    aria-haspopup="dialog" aria-expanded={open}
    title={triggerVariant === "symbol" ? label : undefined}
    aria-label={triggerVariant === "symbol" ? label : undefined}>
    {#if triggerVariant === "symbol"}
      <Icon name={icon} size={18} />
    {:else}
      {label}
    {/if}
    {#if triggerVariant !== "pill" && triggerVariant !== "symbol"}
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    {/if}
  </button>

  {#if open}
    <button
      type="button"
      aria-label={t("common.close", "schließen")}
      use:portal
      class="fixed inset-0 z-30 cursor-default"
      onclick={(e) => { e.preventDefault(); e.stopPropagation(); close(); }}
    ></button>
    <div
      role="dialog"
      tabindex="-1"
      use:anchoredPopover={{ anchor: trigger, align, gap: 4, placement: "auto" }}
      class="border-border bg-bg-elev/95 z-40 w-64 overflow-y-auto rounded-lg border p-3 shadow-xl backdrop-blur-xl"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } }}
    >
      <div class="text-fg-subtle mb-2 t-eyebrow">
        {t("remediation.heading", "Mögliche Maßnahmen")}
      </div>
      <div class="grid gap-3">
        {#each remediations as r (r.id)}
          <div class="grid gap-1.5">
            {#if r.variants && r.variants.length > 0}
              <div class="text-fg t-meta font-medium">{r.label}</div>
              <p class="text-fg-subtle t-meta leading-snug">{r.consequence}</p>
              <div class="grid grid-cols-2 gap-1.5">
                {#each r.variants as v (v.key)}
                  <button type="button" onclick={() => exec(r, v)} disabled={busy}
                    class="border-border hover:bg-bg-muted/60 rounded border ctrl-s.5 text-sm transition disabled:opacity-50">
                    {v.label}
                  </button>
                {/each}
              </div>
            {:else}
              <p class="text-fg-subtle t-meta leading-snug">{r.consequence}</p>
              <button type="button" onclick={() => exec(r)} disabled={busy}
                class="w-full rounded border ctrl-s.5 text-sm font-medium transition disabled:opacity-50
                  {r.tone === 'accent'
                    ? 'bg-accent/15 text-accent border-accent/30'
                    : r.tone === 'danger'
                      ? 'bg-danger/10 text-danger border-danger/30 hover:bg-danger/15'
                      : 'border-border hover:bg-bg-muted/60'}">
                {r.label}
              </button>
            {/if}
          </div>
        {:else}
          <p class="text-fg-subtle t-meta">
            {t("remediation.none", "Keine automatischen Maßnahmen verfügbar.")}
          </p>
        {/each}
      </div>
    </div>
  {/if}
</div>
